import React, { useContext, useState } from 'react'
import {
  UserModelPublicBase,
  WorkspaceModelAsCollaborator
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { t, Trans } from '@lingui/macro'
import IconUser from '../../../../components/common/icon/IconUser'
import {
  Flex,
  Text,
  DrawerItem,
  DrawerItemHeader,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner
} from 'front-end/src/components'
import UserIcon from '../../../../components/common/user/user-icon/UserIcon'
import { IconArrowDown } from '../../../../components/common/icon/IconArrowDown'
import { TopicEditingContext } from '../../../topic-edit-page/TopicEditContext'
import { useTopicAuthorCandidatesLazyQuery } from '../SetAuthorSection.codegen'
import { usePatchTopicMutation } from './ResourceDrawer.codegen'
import { IconArrowUp } from '../../../../components/common/icon/IconArrowUp'
import { brandColors } from '../../../../../../shared/basicPalette'

const SetAuthorSection = ({ author }: { author: UserModelPublicBase }) => {
  const [candidates, setCandidates] = useState<UserModelPublicBase[]>([])
  const { setTopic, topic } = useContext(TopicEditingContext)
  const [loadCandidates] = useTopicAuthorCandidatesLazyQuery({
    variables: { topicId: topic.id },
    onCompleted: (data) => {
      if (data?.topic?.__typename === 'TopicModelAsEditor') {
        setCandidates(
          (data.topic?.workspace as WorkspaceModelAsCollaborator)
            .authorCandidates
        )
      }
    }
  })
  const [patchTopic] = usePatchTopicMutation()

  return (
    <DrawerItem>
      <DrawerItemHeader dataTest="AuthorDrawer">
        <IconUser />
        <Trans>Author</Trans>
      </DrawerItemHeader>

      <Menu matchWidth autoSelect={false}>
        {({ isOpen }) => (
          <>
            <MenuButton
              data-test="AuthorDropdown"
              onClick={() => loadCandidates()}
            >
              <Flex justify="space-between" align="center">
                <Flex align="center">
                  <UserIcon size={30} user={author} />
                  <Text as="span" ml="8px">
                    {author.display_name}
                  </Text>
                </Flex>
                {isOpen ? (
                  <IconArrowUp color={brandColors.grey.mid} />
                ) : (
                  <IconArrowDown color={brandColors.grey.mid} />
                )}
              </Flex>
            </MenuButton>
            <MenuList
              data-test="AuthorListMenu"
              maxH="400px"
              py={0}
              overflow="auto"
            >
              {candidates.length === 0 ? (
                <Flex justifyContent="center">
                  <Spinner size="sm" m="12px 0" />
                </Flex>
              ) : (
                candidates.map((cand) => (
                  <MenuItem
                    data-test="AuthorMenuItem"
                    key={cand.id}
                    onClick={async () => {
                      const { data } = await patchTopic({
                        variables: {
                          topicId: topic.id,
                          input: {
                            user_id: cand.id
                          }
                        }
                      })

                      if (data?.topic) {
                        const { topic: resultTopic } = data
                        if (resultTopic.__typename !== 'TopicModelAsEditor') {
                          throw new Error(
                            t`cannot set author on topic ${resultTopic.id}`
                          )
                        }
                        const newAuthor = candidates.find(
                          (c) => c.id === resultTopic?.patch?.author.id
                        )

                        if (!newAuthor) {
                          throw new Error(
                            t`could not find user ${resultTopic?.patch?.author.id} in the list of candidates`
                          )
                        }
                        if (resultTopic?.__typename === 'TopicModelAsEditor') {
                          setTopic({
                            ...topic,
                            author: newAuthor
                          })
                        }
                      }
                    }}
                  >
                    <UserIcon size={30} user={cand} />
                    <Text as="span" ml="8px">
                      {cand.display_name}
                    </Text>
                  </MenuItem>
                ))
              )}
            </MenuList>
          </>
        )}
      </Menu>
    </DrawerItem>
  )
}

export default SetAuthorSection
